import { PermissionGuard } from '@/components/permission-guard';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { DASHBOARD_SECTIONS, DashboardSectionId, scrollToDashboardSection } from '@/lib/dashboard-sections';
import { AuthService } from '@/services/authService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@opaz/ui-components';
import { useNavigate } from '@tanstack/react-router';
import { Building2, FileText, Handshake, ScrollText, Stamp, type LucideIcon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CONTRACT_PERMISSIONS = [PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ] as const;
const PERMIT_PERMISSIONS = [PROFILE_PERMISSION.PMS_PERMIT, PROFILE_PERMISSION.PMS_READ] as const;

const myServiceOptions: {
  to:
    | '/dashboard/contracts'
    | '/dashboard/rental-contracts'
    | '/dashboard/permits'
    | '/dashboard/service-providers'
    | '/dashboard/properties';
  labelKey:
    | 'dashboard.tabs.contracts'
    | 'dashboard.tabs.rentalContracts'
    | 'dashboard.tabs.permits'
    | 'dashboard.tabs.providers'
    | 'dashboard.tabs.properties';
  icon: LucideIcon;
  hash: DashboardSectionId;
  permissions: readonly (typeof PROFILE_PERMISSION)[keyof typeof PROFILE_PERMISSION][];
}[] = [
  {
    to: '/dashboard/contracts',
    labelKey: 'dashboard.tabs.contracts',
    icon: FileText,
    hash: DASHBOARD_SECTIONS.contracts,
    permissions: CONTRACT_PERMISSIONS,
  },
  {
    to: '/dashboard/rental-contracts',
    labelKey: 'dashboard.tabs.rentalContracts',
    icon: ScrollText,
    hash: DASHBOARD_SECTIONS.rentalContracts,
    permissions: CONTRACT_PERMISSIONS,
  },
  {
    to: '/dashboard/permits',
    labelKey: 'dashboard.tabs.permits',
    icon: Stamp,
    hash: DASHBOARD_SECTIONS.permits,
    permissions: PERMIT_PERMISSIONS,
  },
  {
    to: '/dashboard/service-providers',
    labelKey: 'dashboard.tabs.providers',
    icon: Handshake,
    hash: DASHBOARD_SECTIONS.serviceProviders,
    permissions: CONTRACT_PERMISSIONS,
  },
  {
    to: '/dashboard/properties',
    labelKey: 'dashboard.tabs.properties',
    icon: Building2,
    hash: DASHBOARD_SECTIONS.properties,
    permissions: CONTRACT_PERMISSIONS,
  },
];

export const MY_SERVICES_PATHS = myServiceOptions.map((option) => option.to);

type MyServicesPickerProps = {
  trigger: ReactNode;
};

export function MyServicesPicker({ trigger }: MyServicesPickerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const isAuthenticated = !AuthService.isTokenExpired();

  const chooseService = (option: (typeof myServiceOptions)[number]) => {
    setIsOpen(false);
    void navigate({ to: option.to, hash: option.hash }).then(() => {
      scrollToDashboardSection(option.hash);
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bottom-2 top-auto z-[1200] flex max-h-[78vh] w-[calc(100%-1rem)] max-w-none translate-x-[-50%] translate-y-0 flex-col overflow-hidden rounded-2xl p-5 md:hidden">
        <DialogHeader>
          <DialogTitle>{t('home.myServices')}</DialogTitle>
          <DialogDescription>{t('home.selectMyService')}</DialogDescription>
        </DialogHeader>

        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto py-2">
          {myServiceOptions.map((option) => {
            const Icon = option.icon;
            const item = (
              <li key={option.to}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-2 py-4 text-start font-medium text-link hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  onClick={() => chooseService(option)}
                >
                  <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {t(option.labelKey)}
                </button>
              </li>
            );

            if (!isAuthenticated) return item;

            return (
              <PermissionGuard key={option.to} anyPermission={[...option.permissions]}>
                {item}
              </PermissionGuard>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
